import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  buildPlaybackIssue,
  isMediaPlaybackUnlocked,
  markMediaPlaybackUnlocked,
  PlaybackIssueDetails,
  PlaybackMediaKind,
  unlockMediaPlayback,
  MEDIA_UNLOCK_REQUEST_EVENT,
} from "../lib/mediaPlayback";
import { agentDebugLog } from "../lib/agentDebugLog";
import { videoDebugError, videoDebugLog } from "../lib/videoDebug";
import { playClipAudio, stopClipAudio } from "../lib/clipAudioPlayback";

interface UnifiedVideoPlayerProps {
  baseVideoUrl: string | null;
  /** 旧 segment_text 再生経路の撤去により未使用（後方互換のため受け取りのみ維持）。 */
  responseVideoUrl?: string | null;
  /** 同上。 */
  speakingVideoUrl?: string | null;
  isPlayingResponse: boolean;
  /** 同上（完了は clip の onEnded → onClipEnded で駆動する）。 */
  onResponseEnded?: () => void;
  onPlaybackIssue?: (issue: PlaybackIssueDetails) => void;
  /** WS バイナリで届いた応答 mp4 の Blob URL（映像は muted で再生）。 */
  clipBlobSrc?: string | null;
  /** 上記クリップの音声デコード用バイナリ（Web Audio で audible 再生）。 */
  clipAudioBuffer?: ArrayBuffer | null;
  onClipEnded?: () => void;
}

const UnifiedVideoPlayer: React.FC<UnifiedVideoPlayerProps> = ({
  baseVideoUrl,
  isPlayingResponse,
  onPlaybackIssue,
  clipBlobSrc,
  clipAudioBuffer,
  onClipEnded,
}) => {
  const baseVideoRef = useRef<HTMLVideoElement>(null);
  const clipVideoRef = useRef<HTMLVideoElement>(null);
  const clipAudioStartedRef = useRef(false);
  const lastClipTimeLogAtRef = useRef(0);
  const isPlayingResponseRef = useRef(isPlayingResponse);
  const lastIssueKeyRef = useRef<string>("");
  const [baseVideoCacheUrl, setBaseVideoCacheUrl] = useState<string | null>(null);
  const [visibleClipSrc, setVisibleClipSrc] = useState<string | null>(null);
  const isBlobMode = !!clipBlobSrc;
  const isClipVisible = !!clipBlobSrc && visibleClipSrc === clipBlobSrc;

  // URL の到着や canplay だけでは、画面に出せるフレームがあるとは限らない。
  // 新しい要素の最初のフレームが描画へ渡るまで、背面の待受を表示する。
  useLayoutEffect(() => {
    setVisibleClipSrc(null);
    const clip = clipVideoRef.current;
    if (!clip || !clipBlobSrc) return;

    const frameVideo = clip as HTMLVideoElement & {
      requestVideoFrameCallback?: (callback: () => void) => number;
      cancelVideoFrameCallback?: (id: number) => void;
    };
    let cancelled = false;
    let videoFrameId: number | undefined;
    let animationFrameId: number | undefined;
    const reveal = () => {
      if (cancelled || clipVideoRef.current !== clip || clip.ended || clip.error) return;
      setVisibleClipSrc(clipBlobSrc);
    };

    if (frameVideo.requestVideoFrameCallback) {
      videoFrameId = frameVideo.requestVideoFrameCallback(reveal);
    } else {
      // 古いブラウザでは再生開始後、デコード済み映像と描画の機会を待つ。
      const waitForPaint = () => {
        animationFrameId = requestAnimationFrame(() => {
          if (cancelled || clip.ended || clip.error) return;
          if (clip.readyState >= 2 && clip.videoWidth > 0 && !clip.paused) {
            animationFrameId = requestAnimationFrame(reveal);
          } else {
            waitForPaint();
          }
        });
      };
      waitForPaint();
    }

    return () => {
      cancelled = true;
      if (videoFrameId !== undefined) frameVideo.cancelVideoFrameCallback?.(videoFrameId);
      if (animationFrameId !== undefined) cancelAnimationFrame(animationFrameId);
    };
  }, [clipBlobSrc]);

  useEffect(() => {
    isPlayingResponseRef.current = isPlayingResponse;
  }, [isPlayingResponse]);

  const reportPlaybackIssue = useCallback((issue: PlaybackIssueDetails) => {
    const issueKey = [
      issue.reason,
      issue.mediaKind,
      issue.src,
      issue.readyState,
      issue.errorName,
      issue.errorCode,
      issue.errorMessage,
    ].join("|");

    videoDebugError("応答メディア再生問題:", issue);

    if (lastIssueKeyRef.current !== issueKey) {
      lastIssueKeyRef.current = issueKey;
      onPlaybackIssue?.(issue);
    }
  }, [onPlaybackIssue]);

  const tryPlayMedia = useCallback(async (
    mediaElement: HTMLMediaElement,
    mediaKind: PlaybackMediaKind,
    reasonOnFailure: string,
    options: { reportIssue?: boolean } = {}
  ) => {
    const reportIssueOnFailure = options.reportIssue ?? true;
    try {
      await mediaElement.play();
      markMediaPlaybackUnlocked();
      // #region agent log
      agentDebugLog('H3', 'unified-video-player.tsx:tryPlayMedia', 'play_success', {
        mediaKind,
        reasonOnFailure,
        paused: mediaElement.paused,
        readyState: mediaElement.readyState,
        currentTime: Number.isFinite(mediaElement.currentTime) ? Number(mediaElement.currentTime.toFixed(3)) : null,
        duration: Number.isFinite(mediaElement.duration) ? Number(mediaElement.duration.toFixed(3)) : null,
        muted: mediaElement.muted,
      });
      // #endregion
      return true;
    } catch (error) {
      // #region agent log
      agentDebugLog('H3', 'unified-video-player.tsx:tryPlayMedia', 'play_failed', {
        mediaKind,
        reasonOnFailure,
        errorName: error instanceof Error ? error.name : typeof error,
        errorMessage: error instanceof Error ? error.message.slice(0, 200) : String(error).slice(0, 200),
        paused: mediaElement.paused,
        readyState: mediaElement.readyState,
        currentTime: Number.isFinite(mediaElement.currentTime) ? Number(mediaElement.currentTime.toFixed(3)) : null,
        muted: mediaElement.muted,
        reportIssue: reportIssueOnFailure,
      });
      // #endregion
      if (reportIssueOnFailure) {
        reportPlaybackIssue(buildPlaybackIssue(reasonOnFailure, mediaKind, mediaElement, error));
      }
      return false;
    }
  }, [reportPlaybackIssue]);

  const getPassiveUnlockMediaElements = useCallback(() => [
    baseVideoRef.current,
    clipVideoRef.current,
  ], []);

  // blob クリップ (WS バイナリ経路) の再生開始。
  // 映像は muted 固定で再生する（iOS の autoPlay 許可条件。unmute は t=0 固着の原因のため行わない）。
  // 音声は onPlaying で Web Audio 経由で別途再生する。
  // ここでは停止中のときだけ play() を呼ぶ（autoPlay/loadeddata/canplay からの多重呼び出しでも安全）。
  const startClipPlayback = useCallback(async () => {
    const clip = clipVideoRef.current;
    if (!clip || !clip.paused) return false;
    return tryPlayMedia(clip, "video", "clip-video-play-failed", { reportIssue: false });
  }, [tryPlayMedia]);

  // クリップが切り替わったら音声開始フラグをリセットし、前クリップの Web Audio 音声を止める
  // （要素は key で作り直される）。アンマウント時も停止する。
  useEffect(() => {
    clipAudioStartedRef.current = false;
    return () => { stopClipAudio(); };
  }, [clipBlobSrc]);

  useEffect(() => {
    const unlockCurrentMedia = () => {
      if (isMediaPlaybackUnlocked()) return;
      void unlockMediaPlayback(getPassiveUnlockMediaElements());
    };

    const unlockFromInterviewGesture = () => {
      void unlockMediaPlayback(getPassiveUnlockMediaElements());
    };

    window.addEventListener("pointerdown", unlockCurrentMedia, { capture: true });
    window.addEventListener("touchstart", unlockCurrentMedia, { capture: true });
    window.addEventListener("keydown", unlockCurrentMedia, { capture: true });
    window.addEventListener(MEDIA_UNLOCK_REQUEST_EVENT, unlockFromInterviewGesture);
    return () => {
      window.removeEventListener("pointerdown", unlockCurrentMedia, { capture: true });
      window.removeEventListener("touchstart", unlockCurrentMedia, { capture: true });
      window.removeEventListener("keydown", unlockCurrentMedia, { capture: true });
      window.removeEventListener(MEDIA_UNLOCK_REQUEST_EVENT, unlockFromInterviewGesture);
    };
  }, [getPassiveUnlockMediaElements]);

  // ベース動画（待受ループ）のキャッシュバスティング URL を baseVideoUrl ごとに生成
  useEffect(() => {
    if (!baseVideoUrl) {
      setBaseVideoCacheUrl(null);
      return;
    }
    const cacheUrl = withCacheBust(baseVideoUrl);
    setBaseVideoCacheUrl(cacheUrl);
    videoDebugLog('ベース動画キャッシュURL生成:', cacheUrl);
  }, [baseVideoUrl]);

  // 応答クリップ再生中は待受動画を止め、終了後に再開する。
  useEffect(() => {
    const videoElement = baseVideoRef.current;
    if (!videoElement || !baseVideoCacheUrl) return;

    if (isClipVisible) {
      videoElement.pause();
      return;
    }
    videoElement.play().catch(videoDebugError);
  }, [baseVideoCacheUrl, isClipVisible]);

  const handleBaseVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const videoElement = e.target as HTMLVideoElement;
    videoDebugError('待受動画再生エラー:', {
      src: videoElement.src,
      error: videoElement.error,
    });
  };

  if (!baseVideoUrl && !clipBlobSrc) {
    return (
      <div className="w-full h-full flex items-center justify-center text-white">
        動画を読み込み中...
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex items-center justify-center bg-black overflow-hidden">
      {baseVideoCacheUrl && (
        <video
          ref={baseVideoRef}
          src={baseVideoCacheUrl}
          autoPlay
          loop
          muted
          playsInline
          crossOrigin={resolveVideoCrossOrigin(baseVideoCacheUrl)}
          preload="auto"
          className="absolute inset-0 w-full h-full rounded-lg shadow object-contain"
          style={{
            background: '#000',
            outline: 'none',
            // 待受の最後のフレームを背面に残し、切替時に背景を露出させない。
            opacity: 1,
          }}
          onContextMenu={(e) => e.preventDefault()}
          onError={handleBaseVideoError}
          onCanPlay={() => {
            videoDebugLog('待受動画再生可能:', baseVideoRef.current?.src);
            if (isClipVisible) {
              baseVideoRef.current?.pause();
              return;
            }
            baseVideoRef.current?.play().catch(videoDebugError);
          }}
        />
      )}

      {isBlobMode && clipBlobSrc && (
        <video
          key={clipBlobSrc}
          ref={clipVideoRef}
          src={clipBlobSrc}
          className="absolute inset-0 w-full h-full rounded-lg shadow object-contain"
          style={{ background: '#000', outline: 'none', opacity: isClipVisible ? 1 : 0 }}
          // iPhone Safari は autoPlay/preload の無い <video> をデータロードしないため
          // loadeddata が発火せず再生が始まらない。muted autoPlay + preload=auto で
          // ロード＋無音自動再生を確実に開始させる。映像は muted のまま（unmute はしない＝t=0固着回避）、
          // 音声は onPlaying のタイミングで Web Audio から別途再生する。
          autoPlay
          muted
          preload="auto"
          playsInline
          onContextMenu={(e) => e.preventDefault()}
          onLoadedData={() => { void startClipPlayback(); }}
          onCanPlay={() => { void startClipPlayback(); }}
          onPlaying={() => {
            const clip = clipVideoRef.current;
            markMediaPlaybackUnlocked();
            // 映像の再生開始に合わせて、音声を Web Audio で一度だけ鳴らす（映像は muted のまま）。
            if (!clipAudioStartedRef.current && clipAudioBuffer) {
              clipAudioStartedRef.current = true;
              void playClipAudio(clipAudioBuffer).then((ok) => {
                agentDebugLog('H6', 'unified-video-player.tsx:clip-video', 'clip_audio_start', {
                  src: clipBlobSrc.slice(0, 64),
                  started: ok,
                });
              });
            }
            agentDebugLog('H6', 'unified-video-player.tsx:clip-video', 'clip_playing', {
              src: clipBlobSrc.slice(0, 64),
              muted: clip?.muted ?? null,
              readyState: clip?.readyState ?? null,
              currentTime: clip ? Number(clip.currentTime.toFixed(3)) : null,
              duration: clip && Number.isFinite(clip.duration) ? Number(clip.duration.toFixed(3)) : null,
            });
          }}
          onPause={() => {
            const clip = clipVideoRef.current;
            agentDebugLog('H7', 'unified-video-player.tsx:clip-video', 'clip_pause', {
              src: clipBlobSrc.slice(0, 64),
              ended: clip?.ended ?? null,
              currentTime: clip ? Number(clip.currentTime.toFixed(3)) : null,
              duration: clip && Number.isFinite(clip.duration) ? Number(clip.duration.toFixed(3)) : null,
              readyState: clip?.readyState ?? null,
            });
          }}
          onWaiting={() => {
            agentDebugLog('H7', 'unified-video-player.tsx:clip-video', 'clip_waiting', {
              src: clipBlobSrc.slice(0, 64),
              currentTime: clipVideoRef.current ? Number(clipVideoRef.current.currentTime.toFixed(3)) : null,
              readyState: clipVideoRef.current?.readyState ?? null,
            });
          }}
          onStalled={() => {
            agentDebugLog('H7', 'unified-video-player.tsx:clip-video', 'clip_stalled', {
              src: clipBlobSrc.slice(0, 64),
              currentTime: clipVideoRef.current ? Number(clipVideoRef.current.currentTime.toFixed(3)) : null,
              readyState: clipVideoRef.current?.readyState ?? null,
            });
          }}
          onSuspend={() => {
            agentDebugLog('H7', 'unified-video-player.tsx:clip-video', 'clip_suspend', {
              src: clipBlobSrc.slice(0, 64),
              currentTime: clipVideoRef.current ? Number(clipVideoRef.current.currentTime.toFixed(3)) : null,
              readyState: clipVideoRef.current?.readyState ?? null,
            });
          }}
          onTimeUpdate={() => {
            const now = Date.now();
            if (now - lastClipTimeLogAtRef.current < 2000) return;
            lastClipTimeLogAtRef.current = now;
            const clip = clipVideoRef.current;
            agentDebugLog('H7', 'unified-video-player.tsx:clip-video', 'clip_timeupdate', {
              src: clipBlobSrc.slice(0, 64),
              currentTime: clip ? Number(clip.currentTime.toFixed(3)) : null,
              duration: clip && Number.isFinite(clip.duration) ? Number(clip.duration.toFixed(3)) : null,
              muted: clip?.muted ?? null,
              paused: clip?.paused ?? null,
            });
          }}
          onEnded={() => {
            setVisibleClipSrc(null);
            stopClipAudio();
            agentDebugLog('H7', 'unified-video-player.tsx:clip-video', 'clip_ended', {
              src: clipBlobSrc.slice(0, 64),
            });
            onClipEnded?.();
          }}
          onError={(e) => {
            setVisibleClipSrc(null);
            stopClipAudio();
            const el = e.currentTarget;
            videoDebugError('clip video error', {
              src: clipBlobSrc.slice(0, 64),
              errorCode: el.error?.code ?? null,
              errorMessage: el.error?.message ?? null,
            });
            agentDebugLog('H7', 'unified-video-player.tsx:clip-video', 'clip_error', {
              src: clipBlobSrc.slice(0, 64),
              errorCode: el.error?.code ?? null,
              errorMessage: el.error?.message ?? null,
            });
            onClipEnded?.();
          }}
        />
      )}
    </div>
  );
};

export default UnifiedVideoPlayer;

function withCacheBust(url: string): string {
  const randomParam = Math.random().toString(36).substring(7);
  return url.includes('?') ? `${url}&_cb=${randomParam}` : `${url}?_cb=${randomParam}`;
}

function resolveVideoCrossOrigin(url: string | null | undefined): "anonymous" | undefined {
  if (!url || typeof window === 'undefined') return undefined;
  try {
    const resolved = new URL(url, window.location.href);
    if (resolved.origin === window.location.origin) return undefined;
  } catch {
    return undefined;
  }
  return "anonymous";
}
